import React from 'react';
import { TrendingUp, TrendingDown, BarChart3, Award } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { formatCurrency, formatPercentage, formatAverageHoldDuration } from './formatters';

export interface SymbolPerformanceStat {
  symbol: string;
  totalPnl: number;
  trades: number;
  wins: number;
  totalVolume: number;
  totalQty: number;
  totalHoldHours: number;
  winRate: number;
  avgPnl: number;
  avgHoldHours: number;
  avgSize: number;
}

interface CoinPerformanceSummaryProps {
  symbols: SymbolPerformanceStat[];
}

export const CoinPerformanceSummary: React.FC<CoinPerformanceSummaryProps> = ({ symbols }) => {
  if (symbols.length === 0) {
    return null;
  }

  const sortedByPnL = [...symbols].sort((a, b) => b.totalPnl - a.totalPnl);
  const sortedByTrades = [...symbols].sort((a, b) => b.trades - a.trades);
  const winRateLeaders = symbols.filter((stat) => stat.trades >= 3).sort((a, b) => b.winRate - a.winRate);

  const topPerformer = sortedByPnL[0];
  const bottomPerformer = sortedByPnL[sortedByPnL.length - 1];
  const mostTradedSymbol = sortedByTrades[0];
  const bestWinRateSymbol = winRateLeaders[0];
  const topSymbolsByTrades = sortedByTrades.slice(0, 5);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Coin Performance Summary</h2>
        <p className="text-sm text-gray-600 mt-1">Closed-trade metrics grouped by symbol for the selected period.</p>
      </div>
      <div className="px-6 py-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {topPerformer && (
            <MetricCard
              title="Top Performer"
              valueNode={(
                <>
                  <div>{topPerformer.symbol}</div>
                  <div>{formatCurrency(topPerformer.totalPnl)}</div>
                </>
              )}
              trend="up"
              description={`${topPerformer.trades} trades • ${formatPercentage(topPerformer.winRate)}`}
              icon={<TrendingUp className="h-8 w-8" />}
            />
          )}
          {bottomPerformer && (
            <MetricCard
              title="Bottom Performer"
              valueNode={(
                <>
                  <div>{bottomPerformer.symbol}</div>
                  <div>{formatCurrency(bottomPerformer.totalPnl)}</div>
                </>
              )}
              trend="down"
              description={`${bottomPerformer.trades} trades • ${formatPercentage(bottomPerformer.winRate)}`}
              icon={<TrendingDown className="h-8 w-8" />}
            />
          )}
          {mostTradedSymbol && (
            <MetricCard
              title="Most Trades"
              valueNode={(
                <>
                  <div>{mostTradedSymbol.symbol}</div>
                  <div>{mostTradedSymbol.trades} trades</div>
                </>
              )}
              trend="neutral"
              description={`Volume ${formatCurrency(mostTradedSymbol.totalVolume)} • Avg size ${mostTradedSymbol.avgSize.toFixed(3)}`}
              icon={<BarChart3 className="h-8 w-8" />}
            />
          )}
          {bestWinRateSymbol && (
            <MetricCard
              title="Best Win Rate"
              valueNode={(
                <>
                  <div>{bestWinRateSymbol.symbol}</div>
                  <div>{formatPercentage(bestWinRateSymbol.winRate)}</div>
                </>
              )}
              trend="up"
              description={`${bestWinRateSymbol.trades} trades • Avg hold ${formatAverageHoldDuration(bestWinRateSymbol.avgHoldHours)}`}
              icon={<Award className="h-8 w-8" />}
            />
          )}
        </div>

        {topSymbolsByTrades.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Top Symbols by Trade Count</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trades</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Win Rate</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total P&L</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg P&L</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Size</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Hold</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {topSymbolsByTrades.map((symbol) => (
                    <tr key={symbol.symbol}>
                      <td className="px-4 py-2 font-medium text-gray-900">{symbol.symbol}</td>
                      <td className="px-4 py-2 text-gray-700">{symbol.trades}</td>
                      <td className="px-4 py-2 text-gray-700">{formatPercentage(symbol.winRate)}</td>
                      <td className="px-4 py-2 text-gray-700">{formatCurrency(symbol.totalPnl)}</td>
                      <td className="px-4 py-2 text-gray-700">{formatCurrency(symbol.avgPnl)}</td>
                      <td className="px-4 py-2 text-gray-700">{symbol.avgSize.toFixed(3)}</td>
                      <td className="px-4 py-2 text-gray-700">{formatAverageHoldDuration(symbol.avgHoldHours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
